/*
 * Admin — Cloudinary Storage Audit + Cleanup
 * ------------------------------------------
 *
 * Read-only-by-default UI for the Cloudinary asset audit. Renders:
 *   - Summary card: total assets, in-use, orphan candidates, recent,
 *     duplicate groups, total bytes.
 *   - "Run Audit" button (re-fetches from Cloudinary + Mongo).
 *   - CSV + JSON export links.
 *   - Tabbed tables: In-use (with DB refs), Orphan candidates, Recent
 *     unreferenced (excluded by 72h window), Duplicate groups.
 *   - "Preview Cleanup" dialog (dry-run — never deletes).
 *   - "Execute Cleanup" dialog — requires exact confirm phrase
 *     `DELETE ORPHANED IMAGES` before firing the destructive call.
 *
 * All requests carry the session token from localStorage.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const CONFIRM_PHRASE = "DELETE ORPHANED IMAGES";

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = Number(n);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 ? 2 : 1)} ${units[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Insert a small Cloudinary transformation after `/upload/` so browsers
// load ~80px thumbnails instead of the full-size asset. Falls back to
// the raw secure_url if the pattern doesn't match.
function thumbUrl(url) {
  if (!url || typeof url !== "string") return null;
  const marker = "/upload/";
  const i = url.indexOf(marker);
  if (i < 0) return url;
  const head = url.slice(0, i + marker.length);
  const tail = url.slice(i + marker.length);
  return `${head}w_80,h_80,c_fill,q_auto,f_auto/${tail}`;
}

// Cloudinary console Media Library search — opens the asset in the
// authenticated Cloudinary account UI so you can inspect / delete it
// manually if desired.
function consoleUrl(publicId) {
  if (!publicId) return "#";
  return `https://console.cloudinary.com/console/media_library/search?q=${encodeURIComponent(publicId)}`;
}

function authHeaders() {
  const token = localStorage.getItem("session_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminCloudinaryStoragePage() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState("orphans");
  const [safetyHours, setSafetyHours] = useState(72);

  // Cleanup dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);

  const loadAudit = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/api/admin/cloudinary/audit?safety_hours=${safetyHours}`,
        { headers: { ...authHeaders() } },
      );
      if (!r.ok) {
        toast.error(`Audit failed (${r.status})`);
        setReport(null);
        return;
      }
      setReport(await r.json());
    } catch (e) {
      toast.error(`Audit error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAudit(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const previewCleanup = async () => {
    setDryRunResult(null);
    setConfirmText("");
    setExecutionResult(null);
    try {
      const r = await fetch(`${API}/api/admin/cloudinary/cleanup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ dry_run: true, safety_hours: safetyHours }),
      });
      if (!r.ok) {
        toast.error(`Dry-run failed (${r.status})`);
        return;
      }
      const data = await r.json();
      setDryRunResult(data);
      setShowConfirm(true);
    } catch (e) {
      toast.error(`Dry-run error: ${e.message}`);
    }
  };

  const executeCleanup = async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      toast.error(`Type exactly: ${CONFIRM_PHRASE}`);
      return;
    }
    setExecuting(true);
    try {
      const r = await fetch(`${API}/api/admin/cloudinary/cleanup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          dry_run: false,
          confirm_phrase: confirmText,
          safety_hours: safetyHours,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Cleanup failed");
      } else {
        setExecutionResult(data);
        toast.success(`Deleted ${data.deleted_count} — ${data.failed_count} failed`);
        loadAudit();
      }
    } catch (e) {
      toast.error(`Execute error: ${e.message}`);
    } finally {
      setExecuting(false);
    }
  };

  const summary = report?.summary || null;

  const exportUrl = (format) =>
    `${API}/api/admin/cloudinary/audit/export?format=${format}&safety_hours=${safetyHours}`;

  const openExport = async (format) => {
    const r = await fetch(exportUrl(format), { headers: { ...authHeaders() } });
    if (!r.ok) { toast.error(`Export failed (${r.status})`); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cloudinary_audit.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rows = useMemo(() => {
    if (!report) return [];
    if (tab === "in_use") return report.in_use || [];
    if (tab === "orphans") return report.orphan_candidates || [];
    if (tab === "recent") return report.recent_unreferenced || [];
    return [];
  }, [report, tab]);

  return (
    <div className="min-h-screen p-4 sm:p-8 text-white" style={{ background: "#0e0817" }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin" className="text-sm text-purple-300 hover:underline">← Back to Admin</Link>
            <h1 className="text-3xl font-bold mt-1">☁️ Cloudinary Storage</h1>
            <p className="text-sm text-gray-400 mt-1">
              Read-only audit of Cloudinary assets vs MongoDB references.
              Protected folders: <code>{report?.protected_folders?.join(", ") || "rgm/music"}</code>.
              Safety window: <b>{safetyHours}h</b> (assets younger than this are excluded from orphan list).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={safetyHours}
              min={1}
              max={720}
              onChange={(e) => setSafetyHours(Number(e.target.value) || 72)}
              className="w-24 bg-black/40 border-purple-500/40 text-white"
              data-testid="cloudinary-safety-hours"
              title="Safety window in hours"
            />
            <Button onClick={loadAudit} disabled={loading}
                    data-testid="cloudinary-run-audit"
                    className="bg-purple-600 hover:bg-purple-700 text-white">
              {loading ? "Auditing…" : "Run Audit"}
            </Button>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <StatCard label="Total Assets" value={summary.total_assets} testid="cloudinary-stat-total" />
            <StatCard label="In Use" value={summary.in_use_count} sub={fmtBytes(summary.in_use_bytes)}
                      color="emerald" testid="cloudinary-stat-inuse" />
            <StatCard label="Orphan Candidates" value={summary.orphan_count} sub={fmtBytes(summary.orphan_bytes)}
                      color="amber" testid="cloudinary-stat-orphans" />
            <StatCard label="Recent (excluded)" value={summary.recent_count} sub={fmtBytes(summary.recent_bytes)}
                      color="sky" testid="cloudinary-stat-recent" />
            <StatCard label="Duplicate Groups" value={summary.duplicate_group_count} testid="cloudinary-stat-dupes" />
            <StatCard label="DB references" value={summary.total_referenced_public_ids}
                      testid="cloudinary-stat-refs" />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button variant="outline" onClick={() => openExport("csv")}
                  data-testid="cloudinary-export-csv"
                  className="border-purple-500/40 text-white">
            ⬇ Export CSV
          </Button>
          <Button variant="outline" onClick={() => openExport("json")}
                  data-testid="cloudinary-export-json"
                  className="border-purple-500/40 text-white">
            ⬇ Export JSON
          </Button>
          <Button onClick={previewCleanup}
                  data-testid="cloudinary-preview-cleanup"
                  className="bg-amber-600 hover:bg-amber-700 text-white ml-auto">
            🔍 Preview Cleanup (dry-run)
          </Button>
        </div>

        {summary?.scan_errors?.length > 0 && (
          <Card className="p-3 mb-4 bg-red-900/20 border-red-500/40">
            <div className="text-sm font-semibold text-red-300">Scan errors ({summary.scan_errors.length}):</div>
            <ul className="text-xs text-red-200 mt-1 list-disc list-inside">
              {summary.scan_errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-3 border-b border-purple-900/50">
          {[
            ["orphans", `Orphan candidates (${summary?.orphan_count || 0})`],
            ["in_use", `In use (${summary?.in_use_count || 0})`],
            ["recent", `Recent excluded (${summary?.recent_count || 0})`],
            ["dupes", `Duplicate groups (${summary?.duplicate_group_count || 0})`],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
                    data-testid={`cloudinary-tab-${k}`}
                    className={`px-4 py-2 text-sm ${tab === k
                      ? "text-white border-b-2 border-amber-400"
                      : "text-gray-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "dupes" ? (
          <DupeTable groups={report?.duplicate_groups || []} />
        ) : (
          <AssetTable rows={rows} showRefs={tab === "in_use"} tab={tab} />
        )}

        {/* Confirm dialog */}
        {showConfirm && dryRunResult && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
               data-testid="cloudinary-confirm-dialog">
            <Card className="max-w-2xl w-full p-6 bg-[#1a0f2a] border-amber-500/40 text-white">
              <h2 className="text-xl font-bold mb-2">
                {executionResult ? "✅ Cleanup Complete" : "⚠️ Preview: Cleanup Dry-Run"}
              </h2>
              {!executionResult ? (
                <>
                  <p className="text-sm text-gray-300 mb-4">
                    This will permanently delete <b>{dryRunResult.would_delete_count}</b> orphan assets
                    (<b>{fmtBytes(dryRunResult.would_delete_bytes)}</b>) from Cloudinary. Assets younger
                    than <b>{safetyHours}h</b> and any asset still referenced in MongoDB are skipped
                    automatically.
                  </p>
                  <div className="max-h-40 overflow-y-auto text-xs mb-4 border border-amber-500/20 rounded p-2 bg-black/40">
                    {(dryRunResult.targets || []).slice(0, 25).map((t) => (
                      <div key={t.public_id} className="font-mono text-gray-300">
                        {t.public_id} — {fmtBytes(t.bytes)}
                      </div>
                    ))}
                    {(dryRunResult.targets || []).length > 25 && (
                      <div className="text-gray-500 italic">… and {dryRunResult.targets.length - 25} more</div>
                    )}
                  </div>
                  <div className="text-xs text-amber-300 mb-2">
                    Type <code className="bg-black/50 px-1 rounded">{CONFIRM_PHRASE}</code> to execute:
                  </div>
                  <Input value={confirmText}
                         onChange={(e) => setConfirmText(e.target.value)}
                         data-testid="cloudinary-confirm-input"
                         placeholder={CONFIRM_PHRASE}
                         className="bg-black/40 border-amber-500/40 text-white font-mono" />
                  <div className="flex gap-2 justify-end mt-4">
                    <Button variant="outline" onClick={() => setShowConfirm(false)}
                            data-testid="cloudinary-cancel-cleanup"
                            className="border-gray-500/40 text-white">
                      Cancel
                    </Button>
                    <Button onClick={executeCleanup}
                            disabled={executing || confirmText !== CONFIRM_PHRASE}
                            data-testid="cloudinary-execute-cleanup"
                            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
                      {executing ? "Deleting…" : "Execute Deletion"}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm mb-4">
                    Deleted: <b>{executionResult.deleted_count}</b> — Failed: <b>{executionResult.failed_count}</b>.
                    Manifest saved at <code>{executionResult.manifest_created_at}</code>.
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => { setShowConfirm(false); setDryRunResult(null); setExecutionResult(null); }}
                            data-testid="cloudinary-close-result"
                            className="bg-purple-600 hover:bg-purple-700 text-white">
                      Close
                    </Button>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color = "purple", testid }) {
  const border = {
    purple: "border-purple-500/40",
    amber: "border-amber-500/40",
    emerald: "border-emerald-500/40",
    sky: "border-sky-500/40",
  }[color] || "border-purple-500/40";
  return (
    <Card className={`p-3 bg-black/40 ${border}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-2xl font-bold text-white">{value ?? 0}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </Card>
  );
}

function AssetTable({ rows, showRefs, tab }) {
  if (!rows.length) {
    return <div className="text-sm text-gray-400 py-8 text-center">No assets in this bucket.</div>;
  }
  return (
    <div className="overflow-x-auto rounded border border-purple-900/40">
      <table className="w-full text-xs" data-testid={`cloudinary-table-${tab}`}>
        <thead className="bg-purple-900/30 text-gray-300">
          <tr>
            <th className="p-2 text-left">Preview</th>
            <th className="p-2 text-left">Public ID</th>
            <th className="p-2 text-left">Folder</th>
            <th className="p-2 text-left">Type</th>
            <th className="p-2 text-right">Size</th>
            <th className="p-2 text-left">Created</th>
            <th className="p-2 text-left">
              Last Modified
              <span title="Cloudinary resets this timestamp on every re-upload / overwrite, so it doubles as 'last modified'." className="ml-1 text-gray-500 cursor-help">ⓘ</span>
            </th>
            {showRefs
              ? <th className="p-2 text-left">DB Reference</th>
              : <th className="p-2 text-left">Reason</th>}
            <th className="p-2 text-left">Links</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 500).map((r) => {
            const isImage = (r.resource_type || "").toLowerCase() === "image";
            const thumb = isImage ? thumbUrl(r.secure_url) : null;
            return (
              <tr key={r.public_id} className="border-t border-purple-900/30 text-gray-200 align-top"
                  data-testid={`cloudinary-row-${r.public_id}`}>
                <td className="p-2">
                  {thumb ? (
                    <a href={r.secure_url} target="_blank" rel="noreferrer"
                       title="Open full-size image in new tab">
                      <img src={thumb} alt={r.public_id}
                           loading="lazy"
                           width={64} height={64}
                           className="w-16 h-16 rounded object-cover border border-purple-900/40 bg-black/40"
                           data-testid={`cloudinary-thumb-${r.public_id}`}
                           onError={(e) => { e.currentTarget.style.opacity = "0.2"; }} />
                    </a>
                  ) : (
                    <div className="w-16 h-16 rounded border border-purple-900/40 bg-black/40
                                    flex items-center justify-center text-gray-500 text-[10px]">
                      no preview
                    </div>
                  )}
                </td>
                <td className="p-2 font-mono break-all max-w-[220px]">{r.public_id}</td>
                <td className="p-2">{r.folder}</td>
                <td className="p-2">{r.resource_type}{r.format ? `.${r.format}` : ""}</td>
                <td className="p-2 text-right whitespace-nowrap">{fmtBytes(r.bytes)}</td>
                <td className="p-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="p-2 whitespace-nowrap text-gray-300">{fmtDate(r.created_at)}</td>
                {showRefs
                  ? (
                    <td className="p-2 text-emerald-300">
                      {(r.references || []).map((ref, i) => (
                        <div key={i} className="font-mono">
                          {ref.collection}.{ref.field}
                          <span className="text-gray-400"> #{ref.record_id}</span>
                        </div>
                      ))}
                    </td>
                  )
                  : <td className="p-2 text-amber-300 max-w-[240px]">{r.reason}</td>}
                <td className="p-2 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    <a href={r.secure_url} target="_blank" rel="noreferrer"
                       className="text-sky-300 hover:underline"
                       data-testid={`cloudinary-open-url-${r.public_id}`}>
                      ↗ Direct URL
                    </a>
                    <a href={consoleUrl(r.public_id)} target="_blank" rel="noreferrer"
                       className="text-amber-300 hover:underline"
                       data-testid={`cloudinary-open-console-${r.public_id}`}
                       title="Open in Cloudinary Media Library">
                      ☁ Open in Cloudinary
                    </a>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > 500 && (
        <div className="p-2 text-xs text-gray-500 italic">Showing first 500 of {rows.length}. Export CSV/JSON for the full list.</div>
      )}
    </div>
  );
}

function DupeTable({ groups }) {
  if (!groups.length) {
    return <div className="text-sm text-gray-400 py-8 text-center">No duplicate groups detected.</div>;
  }
  return (
    <div className="space-y-3" data-testid="cloudinary-table-dupes">
      {groups.map((g) => (
        <Card key={g.owner_key} className="p-3 bg-black/40 border-purple-900/40">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-sm text-purple-300">{g.owner_key}</div>
            <div className="text-xs text-gray-400">{g.count} assets</div>
          </div>
          <table className="w-full text-xs">
            <tbody>
              {g.assets.map((a) => (
                <tr key={a.public_id} className="border-t border-purple-900/30 text-gray-200">
                  <td className="p-1 font-mono">{a.public_id}</td>
                  <td className="p-1 text-right">{fmtBytes(a.bytes)}</td>
                  <td className="p-1">{fmtDate(a.created_at)}</td>
                  <td className="p-1">{a.reference_count > 0
                    ? <span className="text-emerald-300">in-use</span>
                    : <span className="text-amber-300">unreferenced</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}
